import { Observable, fromEvent, map, filter, share, merge, EMPTY } from 'rxjs';
import { HostMessage, HostMessageType } from '../interfaces/host-message-types';
import {
  TokenSetupPayload,
  ExternalCommandPayload,
  HostMessagePayload,
} from '../interfaces/host-message-payloads.types';
import { EmbeddedMessage } from '../..';
import { validateHostMessageWithPayload } from '../host-message-validators';
import { ofExternalCommand, ofMessageType } from '../operators';

interface HostApplicationBridge {
  /**
   * Host -> Web channel.
   * In CEF/custom integrations the native host invokes this function to deliver messages.
   * The web app wraps/listens to this function and forwards messages into Rx stream.
   */
  postMessage?: (message: unknown) => void;
  /**
   * Web -> Host channel.
   * The web app calls this from sendMessageToHost in CEF/custom integrations.
   */
  onMessage?: (message: unknown) => void;
}

interface WebkitHostHandler {
  postMessage?: (message: unknown) => void;
  onMessage?: (handler: (message: unknown) => void) => void | (() => void);
  addEventListener?: (type: string, listener: (event: MessageEvent) => void) => void;
  removeEventListener?: (type: string, listener: (event: MessageEvent) => void) => void;
}

interface HostWindow extends Window {
  chrome?: {
    webview?: {
      postMessage: (message: unknown) => void;
    };
  };
  HostApplication?: HostApplicationBridge;
  webkit?: {
    messageHandlers?: {
      host?: WebkitHostHandler;
      HostApplication?: WebkitHostHandler;
    };
  };
}

export interface MessagesStreamOptions {
  /**
   * When set, iframe (`window.postMessage`) messages are accepted only from the parent window and
   * one of these origins; an empty list rejects them all. Native bridges carry no origin and are
   * not filtered. When omitted, window messages are not restricted.
   */
  trustedOrigins?: readonly string[];
}

export class MessagesStream {
  readonly incomingMessages$: Observable<HostMessage>;

  constructor(private readonly options: MessagesStreamOptions = {}) {
    this.incomingMessages$ = this.createMessageStream();
  }

  private isTrustedWindowMessage(event: MessageEvent): boolean {
    const { trustedOrigins } = this.options;
    if (!trustedOrigins) {
      return true;
    }

    const win = globalThis as unknown as HostWindow;
    return (
      win.parent !== win && event.source === win.parent && trustedOrigins.includes(event.origin)
    );
  }

  /**
   * Ensures `window.HostApplication.postMessage` exists.
   *
   * Why:
   * - Keeps the CEF/custom contract discoverable and stable even before the native host attaches.
   * - Allows host teams to always call `window.HostApplication.postMessage(...)`.
   */
  private ensureHostApplicationBridge(win: HostWindow): HostApplicationBridge {
    win.HostApplication ??= {};

    // Host -> Web bootstrap: if native host has not attached yet,
    // create a stable `postMessage` entry point so integrations can target it early.
    if (typeof win.HostApplication.postMessage !== 'function') {
      win.HostApplication.postMessage = () => {
        // Default no-op. Active dispatch is wired while stream has subscribers.
      };
    }

    return win.HostApplication;
  }

  /**
   * Creates the incoming message stream from window message events.
   *
   * Supported Host -> Web channels:
   * - iframe: `window.postMessage`
   * - WebView2: `chrome.webview` `message` event
   * - CEF/custom: `window.HostApplication.postMessage(message)`
   * - WebKit callback/event bridges where available
   */
  private createMessageStream(): Observable<HostMessage> {
    const win = globalThis as unknown as HostWindow;
    const hostApplication = this.ensureHostApplicationBridge(win);

    // Standard window message events (iframe postMessage)
    const windowMessages$ = fromEvent<MessageEvent>(globalThis, 'message').pipe(
      filter(event => this.isTrustedWindowMessage(event))
    );

    // WebView2 message events (.NET embedded)
    const webviewMessages$ = win?.chrome?.webview
      ? fromEvent<MessageEvent>(win.chrome.webview as unknown as EventTarget, 'message')
      : EMPTY;

    // CEF/custom HostApplication.postMessage channel (host -> web)
    const hostApplicationMessages$ = this.createHostApplicationMessageStream(hostApplication);

    // WKWebView/custom WebKit callback-based messages
    const webkitMessages$ = this.createWebkitMessageStream(win);

    return merge(windowMessages$, webviewMessages$, hostApplicationMessages$, webkitMessages$).pipe(
      map(event => this.parseAndValidateMessage(this.extractMessageData(event))),
      // Filter out invalid messages
      filter((msg): msg is HostMessage => msg !== null),
      // Share the stream to avoid multiple listeners
      share()
    );
  }

  /**
   * Creates a stream for CEF/custom host -> web messages.
   *
   * Contract:
   * - Host sends by calling: `window.HostApplication.postMessage(message)`
   * - Web receives by observing this stream
   *
   * Implementation detail:
   * We wrap `HostApplication.postMessage` so each host call is forwarded to subscribers,
   * while preserving the original function behavior.
   */
  private createHostApplicationMessageStream(
    hostApplication: HostApplicationBridge
  ): Observable<unknown> {
    return new Observable<unknown>(subscriber => {
      const originalPostMessage = hostApplication.postMessage;
      const invokeOriginal =
        typeof originalPostMessage === 'function'
          ? originalPostMessage.bind(hostApplication)
          : undefined;

      hostApplication.postMessage = (message: unknown): void => {
        subscriber.next(message);

        try {
          invokeOriginal?.(message);
        } catch (error) {
          console.warn('HostApplication.postMessage threw while dispatching to web stream', error);
        }
      };

      return () => {
        if (typeof originalPostMessage === 'function') {
          hostApplication.postMessage = originalPostMessage;
        }
      };
    });
  }

  /**
   * Creates stream for callback/event-based WebKit host bridges.
   *
   * Some hosts expose custom callback APIs on `webkit.messageHandlers` instead of
   * routing via window events. This keeps compatibility with those integrations.
   */
  private createWebkitMessageStream(win: HostWindow): Observable<unknown> {
    return new Observable<unknown>(subscriber => {
      let teardown: (() => void) | undefined;

      const handler =
        win.webkit?.messageHandlers?.host ?? win.webkit?.messageHandlers?.HostApplication;

      // No WebKit bridge available -> no Host -> Web stream from this channel.
      if (!handler) {
        subscriber.complete();
        return undefined;
      }

      // Host -> Web (WebKit callback form): host invokes registered callback with raw message payload.
      if (typeof handler.onMessage === 'function') {
        const maybeUnsubscribe = handler.onMessage((message: unknown) => subscriber.next(message));
        if (typeof maybeUnsubscribe === 'function') {
          teardown = maybeUnsubscribe;
        }
        return () => {
          teardown?.();
        };
      }

      // Host -> Web (WebKit event form): host emits `message` events carrying payload in `event.data`.
      if (handler.addEventListener) {
        const listener = (event: MessageEvent) => subscriber.next(event);
        handler.addEventListener('message', listener);
        teardown = () => {
          handler.removeEventListener?.('message', listener);
        };
        return () => {
          teardown?.();
        };
      }

      // Bridge exists but exposes no known callback/event API.
      subscriber.complete();
      return undefined;
    });
  }

  /**
   * Extracts message data from event-like objects or raw messages.
   */
  private extractMessageData(event: unknown): unknown {
    // Event-based transports (iframe/WebView2/WebKit event mode) provide payload in `event.data`.
    if (event && typeof event === 'object' && 'data' in event) {
      return (event as { data: unknown }).data;
    }

    // Callback-based transports (CEF/WebKit callback mode) pass raw message directly.
    return event;
  }

  /**
   * Parses and validates an incoming message.
   * Returns null if the message is invalid.
   */
  private parseAndValidateMessage(data: unknown): HostMessage | null {
    const normalizedData = this.normalizeIncomingMessageData(data);

    // Basic structure check
    if (!normalizedData || typeof normalizedData !== 'object') {
      return null;
    }

    const message = normalizedData as EmbeddedMessage;

    // Must have a type
    if (!message.type || typeof message.type !== 'string') {
      return null;
    }

    // Construct host message
    const hostMessage: HostMessage = {
      type: message.type as HostMessageType,
      payload: message.payload as HostMessagePayload,
      timestamp: Date.now(),
    };

    // Validate the complete message
    const validation = validateHostMessageWithPayload(hostMessage);
    if (!validation.valid) {
      console.warn('Invalid host message:', validation.error, normalizedData);
      return null;
    }

    return hostMessage;
  }

  /**
   * Normalizes incoming message data across transports.
   */
  private normalizeIncomingMessageData(data: unknown): unknown {
    // Most transports already provide objects.
    if (typeof data !== 'string') {
      return data;
    }

    try {
      // Some hosts send JSON strings; normalize into object for unified validation.
      return JSON.parse(data) as unknown;
    } catch {
      // Malformed string payloads are treated as invalid messages and filtered out upstream.
      return null;
    }
  }

  /**
   * Returns observable of messages of a specific type.
   */
  onMessageType<T>(type: HostMessageType): Observable<HostMessage<T>> {
    return this.incomingMessages$.pipe(ofMessageType<T>(type));
  }

  /**
   * Returns observable of 'external-command' messages.
   */
  onExternalCommand(): Observable<ExternalCommandPayload> {
    return this.incomingMessages$.pipe(ofExternalCommand());
  }

  /**
   * Returns observable of 'token-setup' messages.
   */
  onTokenSetup(): Observable<TokenSetupPayload> {
    return this.incomingMessages$.pipe(
      ofMessageType<TokenSetupPayload>(HostMessageType.TokenSetup),
      map(msg => msg.payload),
      filter((payload): payload is TokenSetupPayload => !!payload)
    );
  }
}
